import { theme } from "@config";
import {
  useDeleteDeviceMutation,
  useDeleteUserFromOrgMutation,
  useGetDevices,
  useGetUser,
  useGetUsersByOrg,
  useGlobalDeviceContext,
  useReactivateUserInOrgMutation,
} from "@hooks";
import {
  AddCircleOutlined,
  BusinessOutlined,
  Edit,
  PeopleOutlined,
  ShortTextOutlined,
} from "@mui/icons-material";
import { Card, IconButton, Typography } from "@mui/material";
import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { ConfirmationModal, PageBreadcrumb, PageHeader } from "@components";
import { getSubscription } from "src/utils";
import { getOrgFromUser } from "src/utils/user";
import { DeviceCreationModal } from "../components/DeviceCreation/DeviceCreationModal";
import { DEVICE_MODAL_SEARCH_PARAM } from "../components/DeviceManagement/deviceManagementNavigation";
import { DeviceManagementSection } from "../components/DeviceManagement/DeviceManagementSection";
import { FunctionalGroupsSection } from "../components/FunctionalGroups/FunctionalGroupsSection";
import GenericButton from "../components/GenericButton/GenericButton";
import useInviteUser from "../components/ManageOrg/hooks/useInviteUser";
import useUpdateOrgCompanyDescription from "../components/ManageOrg/hooks/useUpdateOrgCompanyDescription";
import useUpdateOrgName from "../components/ManageOrg/hooks/useUpdateOrgName";
import useUpdateUserData from "../components/ManageOrg/hooks/useUpdateUserData";
import Modals from "../components/ManageOrg/Modals";
import UsersTable from "../components/UsersTable/UsersTable";
import { useGetFunctionalGroups } from "../hooks/queries";
import { Loading } from "./Loading";

export const Organization = () => {
  const { orgId = "", deviceId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: user } = useGetUser();
  const org = getOrgFromUser(user, orgId);
  const subscription = org ? getSubscription(org) : null;

  const { data: organizationUsers } = useGetUsersByOrg({
    orgId,
  });
  const { data: devices = [], isLoading: isDevicesLoading } = useGetDevices({
    orgId,
  });
  const { data: functionalGroups = [] } = useGetFunctionalGroups(orgId);
  const { activeGlobalDeviceId, setActiveGlobalDeviceId } =
    useGlobalDeviceContext();

  const { updateOrgNameResponse } = useUpdateOrgName({
    orgId,
    oldName: org?.name ?? "",
  });
  const { updateOrgCompanyDescriptionResponse } =
    useUpdateOrgCompanyDescription({
      orgId,
      oldCompanyDescription: org?.companyDescription ?? "",
    });

  const { inviteUserResponse } = useInviteUser({ orgId });

  const { updateUserDataResponse } = useUpdateUserData({
    orgId,
    editableFunctionalGroups: functionalGroups,
  });

  const deleteUserMutation = useDeleteUserFromOrgMutation(orgId, deviceId);
  const reactivateUserMutation = useReactivateUserInOrgMutation(
    orgId,
    deviceId
  );
  const deleteDeviceMutation = useDeleteDeviceMutation();

  const [pendingMembershipAction, setPendingMembershipAction] = useState<{
    userId: string;
    action: "archive" | "unarchive";
  } | null>(null);

  if (!organizationUsers || !user) return <Loading />;

  const handleArchiveUser = (userId: string) => {
    if (userId === user.id) {
      return;
    }

    setPendingMembershipAction({ userId, action: "archive" });
  };

  const handleUnarchiveUser = (userId: string) => {
    setPendingMembershipAction({ userId, action: "unarchive" });
  };

  const handleConfirmMembershipAction = () => {
    if (!pendingMembershipAction) {
      return;
    }

    if (pendingMembershipAction.action === "archive") {
      deleteUserMutation.mutate({ userId: pendingMembershipAction.userId });
    } else {
      reactivateUserMutation.mutate({
        userId: pendingMembershipAction.userId,
      });
    }
    setPendingMembershipAction(null);
  };

  const setExpandedId = (id: number) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set(DEVICE_MODAL_SEARCH_PARAM, id.toString());
    setSearchParams(nextParams);
  };

  const handleAddDevice = () => {
    setExpandedId(0);
  };

  const handleCloseDeviceModal = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete(DEVICE_MODAL_SEARCH_PARAM);
    setSearchParams(nextParams);
  };

  const handleDeleteDevice = (id: string) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this device? All related data will be lost."
    );

    if (!confirmed) {
      return;
    }

    deleteDeviceMutation.mutate({
      orgId,
      id,
    });
  };

  const settingsCardSx = {
    border: "1px solid rgba(224, 224, 224, 1)",
    fontSize: "1rem",
    boxShadow: "none",
    lineHeight: 2,
    padding: "9px 10px",
    backgroundColor: "transparent",
  } as const;

  const settingsValueSx = {
    color: "text.primary",
    fontSize: "inherit",
    lineHeight: "inherit",
    flex: 1,
  } as const;

  return (
    <div className="flex flex-col self-center w-full gap-6">
      <PageHeader
        pageTitleComponent={
          <PageBreadcrumb items={[{ label: "Organization Settings" }]} />
        }
      />
      <div className="flex flex-col gap-3 py-2 px-3">
        <div className="flex items-center gap-2">
          <BusinessOutlined />
          <Typography variant="body1" data-testid="name-subheader">
            Name
          </Typography>
        </div>
        <Card className="flex items-center gap-2" sx={settingsCardSx}>
          <Typography variant="inherit" sx={settingsValueSx}>
            {org?.name}
          </Typography>
          <IconButton
            aria-label="Edit organization name"
            size="small"
            onClick={() => {
              updateOrgNameResponse.setOpenChangeOrgNameModal(true);
            }}
          >
            <Edit fontSize="small" style={{ color: theme.palette.grey[500] }} />
          </IconButton>
        </Card>
      </div>
      <div className="flex flex-col gap-3 py-2 px-3">
        <div className="flex items-center gap-2">
          <ShortTextOutlined />
          <Typography
            variant="body1"
            data-testid="company-description-subheader"
          >
            Company Description
          </Typography>
        </div>
        <Card className="flex items-center gap-2" sx={settingsCardSx}>
          <Typography
            variant="inherit"
            sx={{ ...settingsValueSx, whiteSpace: "pre-wrap" }}
          >
            {org?.companyDescription || "No company description added yet."}
          </Typography>
          <IconButton
            aria-label="Edit company description"
            size="small"
            onClick={() => {
              updateOrgCompanyDescriptionResponse.setOpenChangeOrgCompanyDescriptionModal(
                true
              );
            }}
          >
            <Edit fontSize="small" style={{ color: theme.palette.grey[500] }} />
          </IconButton>
        </Card>
      </div>
      <DeviceManagementSection
        devices={devices}
        isLoading={isDevicesLoading}
        activeDeviceId={activeGlobalDeviceId}
        onAddDevice={handleAddDevice}
        onDeleteDevice={handleDeleteDevice}
        onSelectDevice={setActiveGlobalDeviceId}
      />
      <FunctionalGroupsSection orgId={orgId} />
      <div className="flex flex-col gap-3 py-2 px-3">
        <div className="flex items-center gap-2">
          <PeopleOutlined />
          <Typography variant="body1" data-testid="members-subheader">
            Members
          </Typography>
          <GenericButton
            id="basic-button"
            variant="contained"
            onClick={() => {
              inviteUserResponse.setOpenInviteUserModal(true);
            }}
            tooltipContent="Add Member"
            color="primary"
          >
            <AddCircleOutlined fontSize="small" color="primary" />
          </GenericButton>
        </div>
        <UsersTable
          rows={organizationUsers}
          orgId={orgId}
          currentUserId={user.id}
          handleEditUserName={(user) => {
            updateUserDataResponse.setSelectedUser(user);
            updateUserDataResponse.setOpenUpdateUserModal(true);
          }}
          handleArchiveUser={handleArchiveUser}
          handleUnarchiveUser={handleUnarchiveUser}
          disableCellSelection
        />
      </div>
      <Modals
        useUpdateOrgName={updateOrgNameResponse}
        useUpdateOrgCompanyDescription={updateOrgCompanyDescriptionResponse}
        useInviteUser={inviteUserResponse}
        useUpdateUserData={updateUserDataResponse}
      />
      <ConfirmationModal
        open={pendingMembershipAction !== null}
        onAbort={() => setPendingMembershipAction(null)}
        onConfirm={handleConfirmMembershipAction}
        confirmText={
          pendingMembershipAction?.action === "unarchive"
            ? "Unarchive"
            : "Archive"
        }
        confirmButtonProps={{
          color:
            pendingMembershipAction?.action === "unarchive"
              ? "primary"
              : "error",
        }}
        title={
          pendingMembershipAction?.action === "unarchive"
            ? "Unarchive this organization member?"
            : "Archive this organization member?"
        }
        content={
          pendingMembershipAction?.action === "unarchive"
            ? "They will regain access to this organization."
            : "They will lose access to this organization but remain visible on historical documents and approvals."
        }
      />
      {org && subscription && (
        <DeviceCreationModal
          open={searchParams.get(DEVICE_MODAL_SEARCH_PARAM) !== null}
          onClose={handleCloseDeviceModal}
          subscription={subscription}
          user={user}
          devices={devices}
          orgId={orgId}
          setExpandedId={setExpandedId}
          expandedId={Number.parseInt(
            searchParams.get(DEVICE_MODAL_SEARCH_PARAM) || "0",
            10
          )}
        />
      )}
    </div>
  );
};
